import React, { forwardRef } from "react";
import { CartItem } from "modules/cart/types/cart";
import { StoreData } from "modules/main/types/storeData";
import { getStoreBdData } from "utils/storeDataManager";

import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator"
import Amount from "utils/amount";

import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";

import { TruckIcon,Sparkles, WandSparkles } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CartCreationStatus, emptyScenario, OptimizedScenario, StoreResume } from "modules/checkout/types/optimizedCart";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { posthog } from "posthog-js";
import { isAndroidApp } from "utils/isAndroidApp";
import { sendToNative } from "utils/native-bridge";

const CREATE_CART_EVENT_CODE = "Crear carrito";

interface CartTableResumeProps extends React.HTMLAttributes<HTMLDivElement>{
    currCart?:CartItem[],
    storeList:StoreData[],
    mode: "VIEW" | "RESULT",
    isMobile?:boolean,
    loading?:boolean
    refreshTrigger?:() => void
    recalculate:boolean,
    setRecalculate:(value:boolean) => void,
    clearCart?:() => void
    selectedIndexScenario:number,
    optimizedScenarios?:OptimizedScenario[],
    setSelectedIndexScenario(value: number, loading: boolean): void,
    storeId?:number,
    cartCreationStatus?:CartCreationStatus[]
    createCartCallBack?: (scenarioId: number, storeId?: number) => void;
    validationLoading?: boolean;
}

interface Totals{
    count:number;
    total:number;
}

export const CartTableResume = forwardRef<HTMLDivElement,CartTableResumeProps>(({optimizedScenarios,storeList,className,mode,...props}, ref)=>{    
    const selectedScenario = optimizedScenarios && optimizedScenarios.length > 0 ? optimizedScenarios[props.selectedIndexScenario] : emptyScenario;
    const {opt_cart, store_resume } = selectedScenario;
    
    const resumePerStore = opt_cart.reduce<ResumeStores>((acc,item)=>{
        item.price_component.filter(pc => !pc.no_met_conditions).forEach((pc) => {
            const {id,price, quantity} = pc;
            if(!acc[id]){
                acc[id] = {
                    count:0,
                    total:0
                }
            }   
            acc[id].count += quantity;
            acc[id].total += price;            
        });
        return acc;
    },{});    


    const totalCart = opt_cart.reduce<Totals>((acc,item) =>{
        item.price_component.filter(pc => !pc.no_met_conditions).forEach((pc) => {
            acc.total += pc.price;
            acc.count += pc.quantity;
        });
        return acc;
    },{
        total:0,
        count:0
    });

    const deliveryTotal = store_resume?.reduce((acc, item) => {
        return acc + item.delivery;
    },0);

    const totalSaving = opt_cart.reduce((acc,item)=>{
        acc += item.price_component.filter(pc => !pc.no_met_conditions).reduce((prev,pc) => {
            return prev + (pc.worst_price - pc.price);
        },0); 
        return acc;
    },0);



    return (
        (!props.isMobile)?
        <ResumeTableComponent ref={ref} selectedScenario={selectedScenario} currCart={props.currCart} storeList={storeList} className={className} mode={mode} {...props}
            resumePerStore={resumePerStore}
            totalCart={totalCart}
            deliveryTotal={deliveryTotal}
            totalSaving={totalSaving}
            loading={props.loading}       
            cartCreationStatus={[]}     

            {...props}
        />:
        <ResumeFooterComponent ref={ref} selectedScenario={selectedScenario} storeList={storeList} className={className} mode={mode} {...props}
            loading={props.loading}
            refreshTrigger={props.refreshTrigger}
            resumePerStore={resumePerStore}
            totalCart={totalCart}
            totalSaving={totalSaving}
            deliveryTotal={deliveryTotal}
            optimizedScenarios={optimizedScenarios}
            cartCreationStatus={props.cartCreationStatus??[]}
            {...props}
        />
    );
});

interface ResumeTableComponentProps extends CartTableResumeProps{
    resumePerStore:ResumeStores,
    selectedScenario?: OptimizedScenario,
    totalCart:Totals,
    deliveryTotal?:number,
    totalSaving?:number,
    recalculate:boolean,
    setRecalculate:(value:boolean) => void,
    setSelectedIndexScenario:(value:number, loading:boolean) => void,
    cartCreationStatus:CartCreationStatus[],
    confirmOrder?:()=>void
}

const ResumeFooterComponent = forwardRef<HTMLDivElement,ResumeTableComponentProps>(({selectedScenario,storeList,className,mode,...props}, ref)=>{        
    const androidApp = isAndroidApp(); 
    const totalProducts = props.cartCreationStatus.reduce((acc, storeStatus) => {
        return acc + storeStatus.products.reduce((sum, product) => {
            const pc = product.price_component.find(pc => pc.id === storeStatus.store_id);
            return sum + (pc ? pc.quantity : 0);
        }, 0);
    }, 0);
    
    const checkedProducts = props.cartCreationStatus.reduce((acc, storeStatus) => {
        return acc + storeStatus.products.reduce((sum, product) => {
            const pc = product.price_component.find(pc => pc.id === storeStatus.store_id);
            return sum + (pc && pc.checked ? pc.quantity : 0);
        }, 0);
    }, 0);

    const handleCheckoutClick = () => {
        posthog.capture(CREATE_CART_EVENT_CODE);
        props.createCartCallBack?.(selectedScenario?.order_id ?? 0, props.storeId);
    }

    const handleCartCreation = () => {
        if (props.createCartCallBack) {
            props.createCartCallBack(selectedScenario?.order_id ?? 0, props.storeId);
            return;
        }

        sendToNative('CREATE_CARTS', {
            order_id: selectedScenario?.order_group_id,
            scenario_id : selectedScenario?.order_id,
        });
    }

    const sendEventPH = () => {
        posthog.capture('Pagar en tienda')
    } 

    if(androidApp){
        return(
            <div className="flex flex-row gap-3 w-full px-4 pt-2  pb-4 sticky bottom-0 border-text-sm">
                <div className={cn("flex flex-col w-[21rem] items-center rounded-full  bg-primary py-3 px-4 text-white text-sm font-semibold gap-2 mx-auto"
                            ,(checkedProducts === totalProducts) ? "bg-green-600" : "bg-primary")
                            }>   
                    <Button  size={"lg"} variant={"ghost"} className="rounded-full" onClick={handleCartCreation}  asChild>
                        <div className="w-full">
                            <Sparkles className="w-16 h-8" />
                            Crear Carritos 
                        </div>
                    </Button>                            
                </div>              
            </div>

        );
    }

    return(
    <>
    <div className="flex flex-row gap-3 w-full px-4 pt-2  pb-4 sticky bottom-0 border-text-sm">
        <div className={cn("flex flex-col w-[15rem] items-center rounded-full  bg-primary py-1 px-4 text-white text-sm font-semibold gap-2 mx-auto"
                            ,(checkedProducts === totalProducts) ? "bg-green-600" : "bg-primary")
                            }> 

            <Button  size={"default"} variant={"ghost"} className="rounded-full" 
                onClick={()=>{
                    sendEventPH();
                    handleCheckoutClick();
                }
                    } >
                <WandSparkles className="w-4 h-4" />
                Crea tus Carritos.
            </Button>     
        </div>
    </div>
    </>
    )
});

const ResumeTableComponent = forwardRef<HTMLDivElement,ResumeTableComponentProps>(({selectedScenario,storeList,className,mode,...props}, ref)=>{
    const handleCheckoutClick = () => {
        props.createCartCallBack?.(selectedScenario?.order_id ?? 0, props.storeId);
    }

    const sendEventPH = () => {
        posthog.capture('Pagar en tienda')
    }

    return (
        <Card className={className}>
        <CardHeader className="flex flex-row items-start bg-muted/50">
        <div className="grid gap-0.5">
            <CardTitle className="group flex items-center gap-2">
            Resumen del pedido
            </CardTitle>
            <CardDescription>Este es el resumen de los costos de tu pedido</CardDescription>
        </div>
        </CardHeader>
        <CardContent className="text-sm p-0">
            <ScrollArea className="h-[31rem] w-full px-6 py-4">            
                <div className="grid gap-3">
                    {
                    (mode==="VIEW")?
                        (<ReviewStoreCard resumePerStore={props.resumePerStore} storeList={storeList}/>):
                        (props.loading)?
                        (<ResumeStoreCardSkeleton/>):
                        (<ResumeStoreCard 
                            resumePerStore={props.resumePerStore} 
                            storeList={storeList} 
                            storeResume={(selectedScenario?.scenarioType === "UNIQUE_STORE")?selectedScenario?.store_resume??[]:selectedScenario?.store_resume?.filter(st => st.met_conditions)??[]}/>)
                    }
                    
                    {
                    (!props.loading)&&
                    (<>
                    <Separator className="my-2" />
                    </>)}
                </div>
            </ScrollArea>    
        </CardContent>
        <CardFooter className="flex flex-col items-center border-t bg-muted/50 px-6 py-3 text-xs gap-2">
            <span className="text-muted-foreground">Podrás crear tus carritos automáticamente.</span>
            <Button
                size={"default"}
                variant={"default"}
                className={`mt-2 mx-auto font-semibold ${(props.loading)?"hidden":""}`}
                onClick={() => {
                    sendEventPH();
                    handleCheckoutClick();
                }}
            >
                <WandSparkles className="w-4 h-4" />
                Crea tus Carritos.
            </Button>
        </CardFooter>
    </Card>
    );
});

interface ResumeStores{
    [key:number]:{
        count:number;
        total:number;
    }
}

interface ReviewStorePros extends React.HTMLAttributes<HTMLDivElement>{
    resumePerStore:ResumeStores
    storeList:StoreData[]
}

const ReviewStoreCard = forwardRef<HTMLDivElement,ReviewStorePros>(({storeList, resumePerStore,...props},ref) => {
    return (
        <div className={cn("",props.className)}>
            <div className="font-semibold">Cantidad de Productos por tienda</div>
            <ul className="grid gap-3 my-2">
            {                
            Object.keys(resumePerStore).map((key) => {
                return (
                <li key={key} className="flex items-center justify-between py-1">
                    <div className="flex flex-row items-center gap-4 min-w-40">
                        <Avatar className="w-8 h-8">
                            <AvatarImage src={getStoreBdData(Number(key),storeList).logo_url}/>
                        </Avatar>
                        <span className="text-muted-foreground max-w-24 line-clamp-1 break-all">{getStoreBdData(Number(key),storeList).name}</span>                                                                                    
                    </div> 
                    <span className="text-muted-foreground"> x <span>{resumePerStore[Number(key)].count}</span></span>
                    <span><Amount amount={resumePerStore[Number(key)].total} currency="CLP" locale="es-CL" /></span>
                </li>
            );
                    }
            )
            }
            </ul>     
        </div>   
    );});

interface ResumeStoreProps extends ReviewStorePros, React.HTMLAttributes<HTMLDivElement>{
    storeResume: StoreResume[]
}

const ResumeStoreCard = forwardRef<HTMLDivElement,ResumeStoreProps> (({storeList, resumePerStore,storeResume,...props},ref) => {
    return (
    <div className={cn("flex flex-col gap-2",props.className)}>
        <span className="text-xs font-semibold">Resumen por tienda</span>
        <ul className="grid gap-2 my-1">
            {                
            storeResume.map((resume, index) => {
                return (
                <li key={index} className="flex flex-row items-start pt-1">
                    <div className="flex flex-col justify-center mx-auto gap-2 min-w-16">
                        <Avatar className="w-8 h-8">
                            <AvatarImage src={getStoreBdData(resume.id,storeList).logo_url}/>
                        </Avatar>
                    </div> 
                    <div className="my-2 flex flex-col w-full gap-2">
                        <div className="flex flex-row justify-between">
                            <span className="text-muted-foreground"> Productos </span>
                            <span>x {resumePerStore[resume.id].count}</span>
                            <span><Amount amount={resumePerStore[resume.id].total} currency="CLP" locale="es-CL" /></span>
                        </div>
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-row justify-between">
                                <span className="text-muted-foreground"> Despacho </span>                                
                                <span><Amount amount={resume.delivery} currency="CLP" locale="es-CL" /></span>
                            </div>
                            {
                            ((resume.min_free_delivery - resumePerStore[resume.id].total > 0) && resume.min_free_delivery < 99999999 )&&(
                            <span className="flex w-full text-xs font-semibold items-center">
                                <TruckIcon className="w-5 h-5 text-yellow-500 mr-2"/>
                                    Incluye + <Amount amount={resume.min_free_delivery - resumePerStore[resume.id].total} currency="CLP" locale="es-CL" className="mx-2"/> 
                                    para despacho gratis
                            </span>
                                )
                            }
                        </div>


                        <Separator className="mt-2" />
                        <div className="flex flex-row justify-between">
                            <div><span className="text-muted-foreground"> Total</span><span className="ml-2 font-semibold">{getStoreBdData(resume.id,storeList).name}</span></div>
                            <span className="font-semibold"><Amount amount={resume.sub_total} currency="CLP" locale="es-CL" /></span>
                        </div>
                    </div>                    
                </li>                
                
            );
                    }
            )
            }
            </ul>
    </div>
    );
});

const ResumeStoreCardSkeleton = forwardRef<HTMLDivElement,React.HTMLAttributes<HTMLDivElement>>((props,ref) => {
    return (
        <div className={cn("flex flex-col gap-2",props.className)}>
            <span className="text-sm font-semibold">Resumen por tienda</span>
            <ul className="grid gap-2 my-1">
                <li className="flex flex-row items-start pt-1">
                    <div className="flex flex-col justify-center mx-auto gap-2 min-w-16">
                        <Skeleton className="h-10 w-10 rounded-full"/>
                    </div> 
                    <div className="my-2 flex flex-col w-full gap-2">
                        <div className="flex flex-row justify-between">
                            <Skeleton className="h-4 w-[50%]"/>
                            <Skeleton className="h-4 w-[20%]"/>
                            <Skeleton className="h-4 w-[20%]"/>
                        </div>
                        <div className="flex flex-row justify-between">
                            <Skeleton className="h-4 w-[50%]"/>
                            <Skeleton className="h-4 w-[20%]"/>
                        </div>
                        <Separator className="mt-2" />
                        <div className="flex flex-row justify-between">
                            <Skeleton className="h-4 w-[50%]"/>
                            <Skeleton className="h-4 w-[20%]"/>
                        </div>
                    </div>
                </li>
                <Separator className="w-full" />
                <li className="flex flex-row items-start pt-1">
                    <div className="flex flex-col justify-center mx-auto gap-2 min-w-16">
                        <Skeleton className="h-10 w-10 rounded-full"/>
                    </div> 
                    <div className="my-2 flex flex-col w-full gap-2">
                        <div className="flex flex-row justify-between">
                            <Skeleton className="h-4 w-[50%]"/>
                            <Skeleton className="h-4 w-[20%]"/>
                            <Skeleton className="h-4 w-[20%]"/>
                        </div>
                        <div className="flex flex-row justify-between">
                            <Skeleton className="h-4 w-[50%]"/>
                            <Skeleton className="h-4 w-[20%]"/>
                        </div>
                        <Separator className="mt-2" />
                        <div className="flex flex-row justify-between">
                            <Skeleton className="h-4 w-[50%]"/>
                            <Skeleton className="h-4 w-[20%]"/>
                        </div>
                    </div>
                </li>                
            </ul>
        </div>
    );
});
